import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { Location } from 'history';
import { Prompt, useHistory } from 'react-router-dom';

export interface PageLeavePromptContextType {
  askChanges: (flag: boolean | (() => boolean)) => void;
  setLeavePromptMessage: (value: string) => void;
  setAllowNavigation: Dispatch<SetStateAction<boolean | ((pathname: string) => boolean)>>;
  resetPromptMessage: () => void;
}

export const PageLeavePromptContext = createContext<PageLeavePromptContextType | null>(
  null,
);

export const defaultMessage =
  'You have unsaved changes, are you sure you want to navigate to another page?';

interface PageLeavePromptProviderProps {
  children: ReactNode;
}

const PageLeavePromptProvider = ({ children }: PageLeavePromptProviderProps) => {
  const history = useHistory();

  const [hasChanges, setHasChanges] = useState<boolean | (() => boolean)>(false);
  const [message, setLeavePromptMessage] = useState<string>(defaultMessage);
  const [allowNavigation, setAllowNavigation] = useState<
    boolean | ((pathname: string) => boolean)
  >(true);

  const checkIfThereAreChanges = useCallback((): boolean => {
    if (typeof hasChanges === 'function') {
      return hasChanges();
    }
    return hasChanges;
  }, [hasChanges]);

  useEffect(() => {
    const unlisten = history.listen(() => {
      setHasChanges(false);
      setAllowNavigation(true);
      setLeavePromptMessage(defaultMessage);
    });

    return () => {
      unlisten();
    };
  }, [history]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (checkIfThereAreChanges()) {
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [checkIfThereAreChanges]);

  const askChanges = useCallback((flag: boolean | (() => boolean)) => {
    if (typeof flag === 'function') {
      setHasChanges(() => flag);
    } else {
      setHasChanges(flag);
    }
  }, []);

  const resetPromptMessage = () => {
    setLeavePromptMessage(defaultMessage);
  };

  return (
    <PageLeavePromptContext.Provider
      value={{
        askChanges,
        setLeavePromptMessage,
        setAllowNavigation,
        resetPromptMessage,
      }}
    >
      <Prompt
        message={(location: Location) => {
          if (typeof allowNavigation === 'function') {
            return allowNavigation(location.pathname) ? true : message;
          }
          if (checkIfThereAreChanges()) {
            return message;
          }
          return true;
        }}
      />
      {children}
    </PageLeavePromptContext.Provider>
  );
};

export default PageLeavePromptProvider;
